import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    avatarColor,
    fullTimestamp,
    initialsOf,
    relTime,
    renderBody,
} from './commentHelpers';

// Inline SVG icons matched to the Rush Studio Editor design.
const Icn = {
    comment: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M2 7a4 4 0 014-4h2a4 4 0 014 4v3a1 1 0 01-1 1H7l-2 2v-2H3a1 1 0 01-1-1V7z"
                stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    ),
    reply: (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M5 3L2 6l3 3M2 6h5a3 3 0 013 3v1"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    resolve: (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 6.5l2.2 2.2L9.5 3.5"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    edit: (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M8.5 2.5l1 1L4 9 2.5 9.5 3 8l5.5-5.5z"
                stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    trash: (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 3.5h7M5 5.5v3M7 5.5v3M3.5 3.5l.5 6a1 1 0 001 1h2a1 1 0 001-1l.5-6M4.5 3.5V2.5a.5.5 0 01.5-.5h2a.5.5 0 01.5.5v1"
                stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    send: (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 6l8-4-2.5 8L6 7 2 6z"
                stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="none" />
        </svg>
    ),
};

// ---------- MentionInput ----------
// Self-contained input matching the design: bordered wrap with an internal
// textarea and right-aligned ghost/send action buttons. Supports @mention
// autocomplete via a popover anchored above the wrap.

const MentionInput = ({
    initial = '',
    placeholder,
    autoFocus,
    collaborators,
    onSend,
    onCancel,
    sendLabel,
}) => {
    const [value, setValue] = useState(initial);
    const [openAt, setOpenAt] = useState(null);
    const [query, setQuery] = useState('');
    const [highlight, setHighlight] = useState(0);
    const textareaRef = useRef(null);

    useEffect(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }, [value]);

    const candidates = useMemo(() => {
        if (openAt == null) return [];
        const q = query.toLowerCase();
        return (collaborators || [])
            .filter((c) => (c.name || '').toLowerCase().startsWith(q))
            .slice(0, 6);
    }, [openAt, query, collaborators]);

    useEffect(() => {
        if (highlight >= candidates.length) setHighlight(0);
    }, [candidates.length, highlight]);

    const recomputeMention = useCallback((nextValue, caret) => {
        let i = caret - 1;
        while (i >= 0) {
            const ch = nextValue[i];
            if (ch === '@') {
                const prev = i === 0 ? ' ' : nextValue[i - 1];
                if (/\s/.test(prev)) {
                    const q = nextValue.slice(i + 1, caret);
                    if (/^[A-Za-z][\w’'\- ]*$/.test(q) || q === '') {
                        setOpenAt(i);
                        setQuery(q);
                        return;
                    }
                }
                break;
            }
            if (/\s/.test(ch)) break;
            i -= 1;
        }
        setOpenAt(null);
        setQuery('');
    }, []);

    const handleChange = (e) => {
        const next = e.target.value;
        setValue(next);
        recomputeMention(next, e.target.selectionStart || next.length);
    };

    const insertMention = (collab) => {
        if (openAt == null || !collab) return;
        const before = value.slice(0, openAt);
        const ta = textareaRef.current;
        const caret = ta ? (ta.selectionStart || value.length) : value.length;
        const after = value.slice(caret);
        const next = `${before}@${collab.name} ${after}`;
        setValue(next);
        setOpenAt(null);
        setQuery('');
        requestAnimationFrame(() => {
            if (!textareaRef.current) return;
            textareaRef.current.focus();
            const pos = before.length + collab.name.length + 2;
            try { textareaRef.current.setSelectionRange(pos, pos); } catch (e) { /* noop */ }
        });
    };

    const send = () => {
        const text = value.trim();
        if (!text) return;
        onSend(text);
        setValue('');
        setOpenAt(null);
    };

    const handleKeyDown = (e) => {
        if (openAt != null && candidates.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % candidates.length); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + candidates.length) % candidates.length); return; }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                insertMention(candidates[highlight]);
                return;
            }
            if (e.key === 'Escape') { e.preventDefault(); setOpenAt(null); setQuery(''); return; }
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
            return;
        }
        if (e.key === 'Escape' && onCancel) { onCancel(); }
    };

    return (
        <div className="cm-input-wrap">
            {openAt != null && candidates.length > 0 && (
                <div className="cm-mention-menu" role="listbox">
                    {candidates.map((c, i) => (
                        <button
                            type="button"
                            key={c._id || c.id}
                            className={`cm-mention-opt ${i === highlight ? 'active' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); insertMention(c); }}
                            onMouseEnter={() => setHighlight(i)}
                        >
                            <span className={`av-s av-${avatarColor(c._id || c.id)}`}>
                                {initialsOf(c.name)}
                            </span>
                            <span>{c.name}</span>
                        </button>
                    ))}
                </div>
            )}
            <textarea
                ref={textareaRef}
                className="cm-textarea"
                rows={1}
                autoFocus={autoFocus}
                value={value}
                placeholder={placeholder}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
            />
            <div className="cm-input-actions">
                {onCancel && (
                    <button
                        type="button"
                        className="cm-mini ghost"
                        onMouseDown={(e) => { e.preventDefault(); onCancel(); }}
                    >
                        Cancel
                    </button>
                )}
                <button
                    type="button"
                    className="cm-mini send"
                    onMouseDown={(e) => { e.preventDefault(); send(); }}
                    disabled={!value.trim()}
                >
                    {Icn.send}{sendLabel || 'Send'}
                </button>
            </div>
        </div>
    );
};

// ---------- Single CommentBlock ----------

const CommentBlock = ({
    comment,
    me,
    isReply,
    editing,
    onStartEdit,
    onCancelEdit,
    onSave,
    onReply,
    onDelete,
    onResolve,
    collaborators,
}) => {
    const authorObj = comment.author || {};
    const authorId = authorObj._id || authorObj.id || '';
    const authorName = `${authorObj.firstName || ''} ${authorObj.lastName || ''}`.trim()
        || authorObj.name
        || authorObj.email
        || 'User';
    const isMine = me && String(authorId) === String(me.id || me._id);
    const isDeleted = !!comment.deleted;

    if (editing) {
        return (
            <div className={`rs-ed-cm-item${isReply ? ' reply' : ''}`}>
                <MentionInput
                    initial={comment.body || ''}
                    autoFocus
                    sendLabel="Save"
                    collaborators={collaborators}
                    onSend={(body) => onSave(comment._id, body)}
                    onCancel={onCancelEdit}
                />
            </div>
        );
    }

    return (
        <div className={`rs-ed-cm-item${isReply ? ' reply' : ''}`}>
            <div className="ch">
                <span className={`av-s av-${avatarColor(authorId)}`}>
                    {initialsOf(authorName)}
                </span>
                <b>{authorName}</b>
                {isMine && <span className="cm-you">you</span>}
                <span className="when" title={fullTimestamp(comment.createdAt)}>
                    {relTime(comment.createdAt)}
                    {comment.editedAt ? ' · edited' : ''}
                </span>
            </div>
            <div className="cb">
                {isDeleted
                    ? <span className="cm-deleted">Comment deleted</span>
                    : renderBody(comment.body, React)}
            </div>
            {!isDeleted && (
                <div className="cm-actions">
                    {!isReply && (
                        <button type="button" onClick={() => onReply(comment._id)}>
                            {Icn.reply} Reply
                        </button>
                    )}
                    {!isReply && (
                        <button type="button" onClick={() => onResolve(comment._id, !comment.resolved)}>
                            {Icn.resolve} {comment.resolved ? 'Reopen' : 'Resolve'}
                        </button>
                    )}
                    {isMine && (
                        <button type="button" onClick={() => onStartEdit(comment._id)}>
                            {Icn.edit} Edit
                        </button>
                    )}
                    {isMine && (
                        <button type="button" className="danger" onClick={() => onDelete(comment)}>
                            {Icn.trash} Delete
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// ---------- CommentsPanel ----------

const CommentsPanel = ({
    sectionIndex,
    state,
    me,
    collaborators,
    onAdd,
    onEdit,
    onDelete,
    onResolve,
}) => {
    const items = state?.items || [];
    const loading = !!state?.loading;
    const error = state?.error || null;

    const { topLevels, repliesByParent } = useMemo(() => {
        const tops = [];
        const replies = {};
        items.forEach((c) => {
            if (!c.parentId) tops.push(c);
            else {
                const k = String(c.parentId);
                replies[k] = replies[k] || [];
                replies[k].push(c);
            }
        });
        return { topLevels: tops, repliesByParent: replies };
    }, [items]);

    const [replyingTo, setReplyingTo] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [expandedResolved, setExpandedResolved] = useState({});

    useEffect(() => {
        setReplyingTo(null);
        setEditingId(null);
        setExpandedResolved({});
    }, [sectionIndex]);

    const segN = (sectionIndex ?? 0) + 1;

    const handleEditSave = (id, body) => {
        onEdit(id, body);
        setEditingId(null);
    };

    return (
        <>
            <div className="rs-ed-cx-sec">
                {topLevels.length === 0
                    ? 'Comments'
                    : `${topLevels.length} thread${topLevels.length > 1 ? 's' : ''} on segment ${segN}`}
            </div>

            {loading && (
                <div className="cm-empty">
                    <span>Loading comments…</span>
                </div>
            )}

            {error && !loading && (
                <div className="cm-empty">
                    <span>Couldn't load comments. {error}</span>
                </div>
            )}

            {!loading && !error && topLevels.length === 0 && (
                <div className="cm-empty">
                    {Icn.comment}
                    <b>No comments yet</b>
                    <span>Start the discussion — @mention a teammate to loop them in.</span>
                </div>
            )}

            {!loading && topLevels.map((top) => {
                const replies = repliesByParent[String(top._id)] || [];
                const isResolved = !top.deleted && top.resolved;
                const expanded = !!expandedResolved[String(top._id)];

                if (isResolved && !expanded) {
                    return (
                        <div
                            key={top._id}
                            className="cm-resolved-row"
                            onClick={() => setExpandedResolved((e) => ({ ...e, [String(top._id)]: true }))}
                        >
                            <span className="cm-resolved-badge">{Icn.resolve} Resolved</span>
                            <span className="cm-resolved-sum">
                                {top.deleted ? 'Comment deleted' : top.body}
                            </span>
                            <span className="cm-resolved-meta">
                                {replies.length ? `${replies.length + 1} messages` : ''}
                            </span>
                        </div>
                    );
                }

                return (
                    <div key={top._id} className={`cm-thread${isResolved ? ' is-resolved' : ''}`}>
                        {isResolved && (
                            <div className="cm-thread-flag">
                                <span className="cm-resolved-badge">{Icn.resolve} Resolved</span>
                                <button
                                    type="button"
                                    className="cm-collapse"
                                    onClick={() => setExpandedResolved((e) => ({ ...e, [String(top._id)]: false }))}
                                >
                                    Collapse
                                </button>
                            </div>
                        )}
                        <CommentBlock
                            comment={top}
                            me={me}
                            isReply={false}
                            editing={editingId === top._id}
                            onStartEdit={(id) => { setEditingId(id); setReplyingTo(null); }}
                            onCancelEdit={() => setEditingId(null)}
                            onSave={handleEditSave}
                            onReply={(id) => { setReplyingTo(replyingTo === id ? null : id); setEditingId(null); }}
                            onDelete={onDelete}
                            onResolve={onResolve}
                            collaborators={collaborators}
                        />
                        {replies.map((r) => (
                            <CommentBlock
                                key={r._id}
                                comment={r}
                                me={me}
                                isReply
                                editing={editingId === r._id}
                                onStartEdit={(id) => { setEditingId(id); setReplyingTo(null); }}
                                onCancelEdit={() => setEditingId(null)}
                                onSave={handleEditSave}
                                onReply={() => {}}
                                onDelete={onDelete}
                                onResolve={() => {}}
                                collaborators={collaborators}
                            />
                        ))}
                        {String(replyingTo) === String(top._id) && (
                            <div className="cm-reply-box">
                                <MentionInput
                                    autoFocus
                                    placeholder={`Reply to ${(top.author?.firstName || top.author?.name || 'thread')}…`}
                                    sendLabel="Reply"
                                    collaborators={collaborators}
                                    onSend={(body) => {
                                        onAdd({ sectionIndex, body, parentId: top._id });
                                        setReplyingTo(null);
                                    }}
                                    onCancel={() => setReplyingTo(null)}
                                />
                            </div>
                        )}
                    </div>
                );
            })}

            <div className="rs-ed-cm-compose">
                <span className={`av-s av-${avatarColor(me?.id || me?._id)}`}>
                    {initialsOf(`${me?.firstName || ''} ${me?.lastName || ''}`.trim() || me?.name || me?.email || '?')}
                </span>
                <MentionInput
                    placeholder="Comment on this segment…  @ to mention"
                    collaborators={collaborators}
                    onSend={(body) => onAdd({ sectionIndex, body, parentId: null })}
                />
            </div>
        </>
    );
};

export default CommentsPanel;
